import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoWalletOutline, IoNotificationsOutline, IoFolderOpenOutline, IoChevronForward } from 'react-icons/io5';
import InfoTooltip from '../../../components/common/InfoTooltip';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionSummary } from '../../../services/commissionService';
import { getSiteSettings } from '../../../services/settingsService';
import { buildContractRevenueRows } from '../../../utils/revenueCalc';
import { db } from '../../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

const fmt = (n) => (Number(n) || 0).toLocaleString();

// 다음 정산 예정일 (이번달 말일, 말일 지났으면 다음달 말일)
const getNextPayoutText = () => {
  const now = new Date();
  const thisMonthLast = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const target = now.getDate() >= thisMonthLast.getDate()
    ? new Date(now.getFullYear(), now.getMonth() + 2, 0)
    : thisMonthLast;
  return `${target.getMonth() + 1}월 ${target.getDate()}일`;
};

const ManageSettlementTab = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [areaInput, setAreaInput] = useState(20);
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState({ withdrawable: 0, total: 0, conversion: 0, referral: 0, settled: 0 });
  const [pendingWithdraw, setPendingWithdraw] = useState(0);

  useEffect(() => {
    getSiteSettings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    if (!userData?.uid) return;
    getCommissionSummary(userData.uid).then(setSummary).catch(() => {});
  }, [userData?.uid]);

  useEffect(() => {
    if (!userData?.uid) return;
    (async () => {
      try {
        const q = query(
          collection(db, 'withdrawal_requests'),
          where('realtorId', '==', userData.uid),
          where('status', 'in', ['requested', 'approved']),
        );
        const snap = await getDocs(q);
        const sum = snap.docs.reduce((acc, d) => acc + (d.data().amount || 0), 0);
        setPendingWithdraw(sum);
      } catch { /* skip */ }
    })();
  }, [userData?.uid]);

  const totalReward = (summary.total || 0);
  const payoutText = getNextPayoutText();

  const { rows, shares } = useMemo(
    () => buildContractRevenueRows(settings, areaInput || 0),
    [settings, areaInput],
  );
  const pickedSum = (shares[0] || 0) + (shares[3] || 0) + (shares[5] || 0) + (shares[6] || 0);
  return (
    <Wrapper>
      <Desc>정산금 현황 · 출금 가능액 및 수익금 내역</Desc>

      {/* 총 보유 리워드 카드 */}
      <WithdrawCard>
        <IconCircle>
          <IoWalletOutline size={20} color="#fff" />
        </IconCircle>
        <WithdrawLabel>총 보유 리워드</WithdrawLabel>
        <WithdrawAmount>{fmt(totalReward)} 원</WithdrawAmount>
        <WithdrawNote>현재 보유중인 전체 리워드 입니다.</WithdrawNote>
      </WithdrawCard>

      {/* 리워드 2x2 그리드 */}
      <Grid>
        <GridCard>
          <GridLabel>출금요청 가능 리워드</GridLabel>
          <GridAmount>{fmt(summary.withdrawable)} 원</GridAmount>
          <GridSub>전월까지 적립 리워드중 미 출금금액</GridSub>
        </GridCard>
        <GridCard>
          <GridLabel>출금요청한 리워드</GridLabel>
          <GridAmount>{fmt(pendingWithdraw)} 원</GridAmount>
          <GridSub>{payoutText} 지정 통장으로 입금예정</GridSub>
        </GridCard>
        <GridCard>
          <GridLabel>당월 적립된 추천 리워드</GridLabel>
          <GridAmount>{fmt(summary.conversion)} 원</GridAmount>
          <GridSub>계약완료 고객 전환 수익금</GridSub>
        </GridCard>
        <GridCard>
          <GridLabel>당월 추천 공인중개사 수익금</GridLabel>
          <GridAmount>{fmt(summary.referral)} 원</GridAmount>
          <GridSub>누적 수익금 {fmt(summary.referral)}원</GridSub>
        </GridCard>
      </Grid>
      <Divider />

      {/* 알림 안내 + 토글 */}
      <NotifRow>
        <NotifLeft>
          <NotifTitle>
            <IoNotificationsOutline size={18} color="#F59E0B" />
            수익금 증가 시 알림
          </NotifTitle>
          <NotifDesc>수익금이 증가하면 즉시 알림을 보냅니다.</NotifDesc>
        </NotifLeft>
        <ToggleLabel>
          <ToggleInput type="checkbox" defaultChecked />
          <ToggleSlider />
        </ToggleLabel>
      </NotifRow>

      {/* 출금 신청하기 버튼 */}
      <WithdrawButton onClick={() => navigate('/realtor/mypage/withdraw')}>출금 신청하기</WithdrawButton>

      {/* 계좌 안내 */}
      <AccountNote>계좌 인증 완료 후 출금 신청 가능</AccountNote>

      {/* 계좌 인증 안내 + 툴팁 */}
      <TooltipRow>
        <TooltipText>온라인 계좌 인증이 어렵다면?</TooltipText>
        <InfoTooltip
          align="right"
          text={`기업 전용 메일 : biz@ieum.in\n온라인 계좌인증이 어렵다면 위 메일로\n• 통장 사본(본사 상호에 준함)\n• 신분증 사본\n• 사업자등록증\n을 보내주세요.\n담당 직원이 인증을 처리해드립니다.`}
        />
      </TooltipRow>

      {/* 마케팅 리워드 적립기준 */}
      <RewardSection>
        <RewardTitle>
          <IoFolderOpenOutline size={18} color="#F59E0B" />
          마케팅 리워드 적립기준
        </RewardTitle>
        <RewardList>
          <RewardItem>1. 계약완료 수치에따른 기여도별 리워드 분배(본사 상황에 준함)</RewardItem>
          <RewardItem>2. 초대한 공인중개사의 리워드 금액의 5%(1년간 적립)</RewardItem>
        </RewardList>
      </RewardSection>

      {/* 계약완료 수익 분배 테이블 */}
      <ContribSection>
        <ContribHeader>
          <ContribTitle>계약완료 수익 분배 테이블</ContribTitle>
          <AreaInputWrap>
            <AreaInput
              type="number"
              value={areaInput}
              onChange={(e) => setAreaInput(Math.max(0, Number(e.target.value) || 0))}
              min="0"
              max="200"
              inputMode="numeric"
            />
            <AreaUnit>평</AreaUnit>
          </AreaInputWrap>
        </ContribHeader>
        <ContribDesc>평수 입력 시 수익이 자동 계산됩니다. (기본 20평 기준)</ContribDesc>
        <ContribTable>
          <thead>
            <tr>
              <ContribTh>구분</ContribTh>
              <ContribTh $align="right">내 수익</ContribTh>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.name}>
                <ContribTd>{r.name}</ContribTd>
                <ContribTd $primary $align="right">{shares[i].toLocaleString()} 원</ContribTd>
              </tr>
            ))}
          </tbody>
        </ContribTable>
        <TotalRow>
          <span>{areaInput || 0}평 단일 서비스 최대 수익</span>
          <TotalPrice>{pickedSum.toLocaleString()} 원</TotalPrice>
        </TotalRow>
        <TotalHint>포장이사 + 입주청소 + 인터넷·TV + 인테리어 시 수익 예시</TotalHint>
      </ContribSection>

    </Wrapper>
  );
};

export default ManageSettlementTab;

const Wrapper = styled.div``;

const Desc = styled.p`
  font-size: 13px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const WithdrawCard = styled.div`
  background: linear-gradient(150deg, #036EB8 0%, #036EB8 55%, #F59E0B 100%);
  border-radius: 12px;
  padding: 10px 20px 10px;
  text-align: center;
  margin-top: 16px;
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 4px;
`;

const WithdrawLabel = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;

const WithdrawAmount = styled.p`
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  margin-top: 0;
`;

const WithdrawNote = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
`;

const GridCard = styled.div`
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 12px 14px;
  min-width: 0;
  overflow: hidden;
`;

const GridLabel = styled.p`
  font-size: 12px;
  color: #6B7280;
  word-break: keep-all;
  line-height: 1.35;
`;

const GridAmount = styled.p`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #1F2937;
  margin-top: 4px;
  white-space: nowrap;
`;

const GridSub = styled.p`
  font-size: 10px;
  color: #9CA3AF;
  margin-top: 2px;
  word-break: keep-all;
  line-height: 1.4;
`;

const TotalSection = styled.div`
  margin-top: 24px;
`;

const TotalLabel = styled.p`
  font-size: 13px;
  color: #6B7280;
`;

const TotalAmount = styled.p`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #1F2937;
  margin-top: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background: #F3F4F6;
  margin: 24px 0;
`;

const NotifRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NotifLeft = styled.div`
  flex: 1;
`;

const NotifTitle = styled.p`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #1F2937;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NotifDesc = styled.p`
  font-size: 12px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-top: 4px;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span { background: #4A6CF7; }
  &:checked + span::before { transform: translateX(22px); }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #D1D5DB;
  border-radius: 26px;
  transition: 0.2s;

  &::before {
    content: '';
    position: absolute;
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: 0.2s;
  }
`;

const WithdrawButton = styled.button`
  width: 100%;
  background: #4A6CF7;
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 24px;
`;

const AccountNote = styled.p`
  font-size: 12px;
  color: #6B7280;
  text-align: center;
  margin-top: 12px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
`;

const TooltipText = styled.span`
  font-size: 12px;
  color: #4A6CF7;
`;

const RewardSection = styled.div`
  margin-top: 32px;
`;

const RewardTitle = styled.h3`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #1F2937;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RewardList = styled.div`
  margin-top: 12px;
  padding-left: 8px;
`;

const RewardItem = styled.p`
  font-size: 12px;
  color: #6B7280;
  line-height: 1.6;
  margin-top: 4px;
`;

const DetailLinkBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 24px;
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;
  cursor: pointer;

  &:hover {
    background: #F9FAFB;
  }
`;

const ContribSection = styled.div`
  margin-top: 32px;
`;

const ContribTitle = styled.h3`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #1F2937;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ContribHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const AreaInputWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const AreaInput = styled.input`
  width: 60px;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  text-align: right;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  &:focus { outline: none; border-color: #4A6CF7; }
`;

const AreaUnit = styled.span`
  font-size: 12px;
  color: #6B7280;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 12px 14px;
  background: #F9FAFB;
  border-radius: 8px;
  font-size: 13px;
  color: #6B7280;
`;

const TotalPrice = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #4A6CF7;
`;

const TotalHint = styled.p`
  margin: 6px 2px 0;
  font-size: 12px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const ContribDesc = styled.p`
  font-size: 12px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-top: 4px;
`;

const ContribTable = styled.table`
  width: 100%;
  margin-top: 12px;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
`;

const ContribTh = styled.th`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: #6B7280;
  text-align: ${({ $align }) => $align || 'left'};
  padding: 10px 10px;
  white-space: nowrap;
  border-bottom: 1px solid #F3F4F6;
  background: #FAFBFC;
`;

const ContribTd = styled.td`
  font-size: 13px;
  color: ${({ $muted, $primary, theme }) =>
    $primary ? theme.colors.primary : $muted ? '#9CA3AF' : '#1F2937'};
  font-weight: ${({ $primary, theme }) => ($primary ? theme.fontWeight.semibold : theme.fontWeight.regular)};
  text-align: ${({ $align }) => $align || 'left'};
  padding: 10px 10px;
  white-space: nowrap;
  border-bottom: 1px solid #F3F4F6;

  tr:last-child & {
    border-bottom: none;
  }
`;
